import {version} from './version';
import {Logger} from '../logger';
import {inject} from '../di';

export function importStyle(
  sSrc: string,
  sId?: string,
  fOnload?: () => void,
  fOnerror?: () => void,
  strictCacheControl?: boolean,
  useWarnForErrors?: boolean,
) {
  const logger = inject(Logger);
  return new Promise<void>((resolve, reject) => {
    try {
      logger.debug(`[importStyle] CSS file loading started: "${sSrc}"`);

      const prefix = sSrc.includes('?') ? `&` : '?';
      const cacheControlSuffix = strictCacheControl ? `${prefix}c=${Date.now()}` : `${prefix}v=${version.v}`;
      const cssUrl = sSrc + cacheControlSuffix;

      // We load styles via XHR (and not by dynamic link tag insertion), because on older browsers the `onerror` and
      //  `onload` events for the link tag may not work (never fired).
      $.ajax({
        url: cssUrl,
        dataType: 'text',
        timeout: 30000,
      })
        .done((cssContent: string) => {
          addStyleTag(cssContent, sId);
          logger.debug(`[importStyle] CSS file was loaded successfully: "${sSrc}"`);
          resolve();
          if (fOnload && typeof fOnload === 'function') {
            fOnload();
          }
        })
        .fail((jqXHR, textStatus, errorThrown) => {
          if (sId) {
            // Legacy code can use link tags with specific IDs to place content. That's why we add an empty tag.
            addStyleTag('', sId);
          }

          const msg = `[importStyle] Unable to load CSS file: "${cssUrl}"`;
          if (useWarnForErrors) {
            logger.warn(msg, textStatus, errorThrown);
          } else {
            logger.error(msg, textStatus, errorThrown);
          }

          reject();
          if (fOnerror && typeof fOnerror === 'function') {
            fOnerror();
          }
        });
    } catch (err) {
      if (sId) {
        // Legacy code can use link tags with specific IDs to place content. That's why we add an empty tag.
        addStyleTag('', sId);
      }
      logger.critical(`[importStyle] Unexpected error while loading CSS file "${sSrc}"`, err);
      reject();
      if (fOnerror && typeof fOnerror === 'function') {
        fOnerror();
      }
    }
  });
}

function addStyleTag(cssContent: string, sId?: string) {
  const styleElement = document.createElement('style');
  styleElement.type = 'text/css';
  if (sId) {
    styleElement.id = sId;
  }

  // Add CSS content to the style element
  styleElement.textContent = cssContent || '';

  const oHead = document.head || document.getElementsByTagName('head')[0];
  oHead.appendChild(styleElement);
}

window.importStyle = importStyle;
